import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import EmployeeLayout from "@/components/EmployeeLayout";
import ProviderLayout from "@/components/ProviderLayout";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Employees from "@/pages/Employees";
import EmployeeForm from "@/pages/EmployeeForm";
import EmployeeDetail from "@/pages/EmployeeDetail";
import Departments from "@/pages/Departments";
import Designations from "@/pages/Designations";
import Documents from "@/pages/Documents";
import Payroll from "@/pages/Payroll";
import Compliance from "@/pages/Compliance";
import Approvals from "@/pages/Approvals";
import LeaveAdmin from "@/pages/LeaveAdmin";
import Grievances from "@/pages/Grievances";
import OnDuty from "@/pages/OnDuty";
import HolidayAdmin from "@/pages/HolidayAdmin";
import Users from "@/pages/Users";
import AuditLogs from "@/pages/AuditLogs";
import Notifications from "@/pages/Notifications";
import AttendanceLocations from "@/pages/AttendanceLocations";
import AttendanceMonitor from "@/pages/AttendanceMonitor";
import WeeklyOffRoster from "@/pages/WeeklyOffRoster";
import LetterLibrary from "@/pages/LetterLibrary";
import PolicyLibrary from "@/pages/PolicyLibrary";
import IssuedLetters from "@/pages/IssuedLetters";
import ServiceProviders from "@/pages/ServiceProviders";
import ConsultantRemuneration from "@/pages/ConsultantRemuneration";
import ProviderWork from "@/pages/provider/ProviderWork";
import ProviderPayouts from "@/pages/provider/ProviderPayouts";
import ProviderProfile from "@/pages/provider/ProviderProfile";
import EmployeeDashboard from "@/pages/employee/EmployeeDashboard";
import MyProfile from "@/pages/employee/MyProfile";
import MyResponsibilities from "@/pages/employee/MyResponsibilities";
import MyAuthorizations from "@/pages/employee/MyAuthorizations";
import MyQualifications from "@/pages/employee/MyQualifications";
import MyDocuments from "@/pages/employee/MyDocuments";
import MyLeave from "@/pages/employee/MyLeave";
import MyGrievances from "@/pages/employee/MyGrievances";
import MyOnDuty from "@/pages/employee/MyOnDuty";
import MyTeam from "@/pages/employee/MyTeam";
import HolidayCalendar from "@/pages/employee/HolidayCalendar";
import RequestCorrection from "@/pages/employee/RequestCorrection";
import MyAttendance from "@/pages/employee/MyAttendance";
import MyNotifications from "@/pages/employee/MyNotifications";
import MyPayslips from "@/pages/employee/MyPayslips";
import MyPolicies from "@/pages/employee/MyPolicies";
import MyLetters from "@/pages/employee/MyLetters";
import { Loader2 } from "lucide-react";

function AdminRoutes() {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/employees" element={<Employees />} />
        <Route path="/employees/new" element={<EmployeeForm />} />
        <Route path="/employees/:id" element={<EmployeeDetail />} />
        <Route path="/employees/:id/edit" element={<EmployeeForm />} />
        <Route path="/departments" element={<Departments />} />
        <Route path="/designations" element={<Designations />} />
        <Route path="/documents" element={<Documents />} />
        <Route path="/letter-library" element={<LetterLibrary />} />
        <Route path="/policies" element={<PolicyLibrary />} />
        <Route path="/issued-letters" element={<IssuedLetters />} />
        <Route path="/payroll" element={<Payroll />} />
        <Route path="/compliance" element={<Compliance />} />
        <Route path="/leave" element={<LeaveAdmin />} />
        <Route path="/grievances" element={<Grievances />} />
        <Route path="/on-duty" element={<OnDuty />} />
        <Route path="/holidays" element={<HolidayAdmin />} />
        <Route path="/attendance-locations" element={<AttendanceLocations />} />
        <Route path="/attendance-today" element={<AttendanceMonitor />} />
        <Route path="/weekly-off-roster" element={<WeeklyOffRoster />} />
        <Route path="/approvals" element={<Approvals />} />
        <Route path="/service-providers" element={<ServiceProviders />} />
        <Route path="/consultants" element={<ConsultantRemuneration />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/users" element={<Users />} />
        <Route path="/audit" element={<AuditLogs />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}

function EmployeeRoutes() {
  return (
    <EmployeeLayout>
      <Routes>
        <Route path="/" element={<EmployeeDashboard />} />
        <Route path="/attendance" element={<MyAttendance />} />
        <Route path="/profile" element={<MyProfile />} />
        <Route path="/team" element={<MyTeam />} />
        <Route path="/responsibilities" element={<MyResponsibilities />} />
        <Route path="/authorizations" element={<MyAuthorizations />} />
        <Route path="/qualifications" element={<MyQualifications />} />
        <Route path="/leave" element={<MyLeave />} />
        <Route path="/grievances" element={<MyGrievances />} />
        <Route path="/on-duty" element={<MyOnDuty />} />
        <Route path="/holidays" element={<HolidayCalendar />} />
        <Route path="/documents" element={<MyDocuments />} />
        <Route path="/payslips" element={<MyPayslips />} />
        <Route path="/policies" element={<MyPolicies />} />
        <Route path="/letters" element={<MyLetters />} />
        <Route path="/notifications" element={<MyNotifications />} />
        <Route path="/requests" element={<RequestCorrection />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </EmployeeLayout>
  );
}

// Independent service providers get their own minimal portal — never the
// employee self-service shell (no attendance, leave, policies or payslips).
function ProviderRoutes() {
  return (
    <ProviderLayout>
      <Routes>
        <Route path="/" element={<ProviderWork />} />
        <Route path="/payouts" element={<ProviderPayouts />} />
        <Route path="/profile" element={<ProviderProfile />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ProviderLayout>
  );
}

function Shell() {
  const { user, isEmployee, isProvider, inEmployeeView } = useAuth();
  if (user === null) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-navy" /></div>;
  if (user === false)
    return <Routes><Route path="/login" element={<Login />} /><Route path="*" element={<Navigate to="/login" replace />} /></Routes>;
  if (isProvider) return <ProviderRoutes />;
  if (isEmployee || inEmployeeView) return <EmployeeRoutes />;
  return <AdminRoutes />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Shell />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
